import React, { useState, useEffect } from 'react'
import { useOnboardingStore } from '../../../stores/onboardingStore'
import { Button } from '../../../components/ui/Button'
import { Card } from '../../../components/ui/Card'
import { Badge } from '../../../components/ui/Badge'
import { ServiceConfigUtils } from '../../../config/services.utils'
import { ServiceConfig } from '../../../config/types/service.types'
import {
  CheckCircle,
  Zap,
  TrendingUp,
  BarChart3,
  Target,
  Clock,
  FileText,
  Download,
  Star,
  Info,
  Award
} from 'lucide-react'

const formatPriceWithSmallDecimals = (priceDisplay: string) => {
  // Split price like "$49.00 USD" into parts
  const match = priceDisplay.match(/^(\$\d+)(\.00)(\s+\w+)$/)
  if (!match) return priceDisplay // Return as-is if format doesn't match

  const [, dollars, cents, currency] = match
  return { dollars, cents, currency }
}

export const ServiceSelectionScreen: React.FC = () => {
  const { data, updateData } = useOnboardingStore()
  const [selectedService, setSelectedService] = useState<string>(data.selectedService?.id || '')
  const [services, setServices] = useState<ServiceConfig[]>([])

  useEffect(() => {
    // Load active services from configuration
    const activeServices = ServiceConfigUtils.getActiveServices()
    setServices(activeServices)
    
    // Pre-select free service if no selection exists and auto-save it
    if (!selectedService && activeServices.length > 0) {
      const freeService = activeServices.find(s => s.price.value === 0)
      if (freeService) {
        setSelectedService(freeService.id)
        // Auto-save the free service selection
        updateData('selectedService', {
          id: freeService.id,
          name: freeService.name,
          price: freeService.price.value,
          tierLevel: freeService.tierLevel || 'quick',
          billingType: freeService.billingType, // Capture billing type from services.config.ts
          deliveryFormats: Object.keys(freeService.metadata.specifications.documents.formats)
            .filter(format => freeService.metadata.specifications.documents.formats[format as keyof typeof freeService.metadata.specifications.documents.formats])
        })
      }
    }
  }, [])

  const handleServiceSelect = (serviceId: string) => {
    setSelectedService(serviceId)
    
    // Immediately save the service selection to store
    const service = services.find(s => s.id === serviceId)
    if (service) {
      updateData('selectedService', {
        id: service.id,
        name: service.name,
        price: service.price.value,
        tierLevel: service.tierLevel || 'standard',
        billingType: service.billingType, // Capture billing type from services.config.ts
        deliveryFormats: Object.keys(service.metadata.specifications.documents.formats)
          .filter(format => service.metadata.specifications.documents.formats[format as keyof typeof service.metadata.specifications.documents.formats])
      })
    }
  }


  const getTierBadgeConfig = (tierLevel?: string) => {
    switch (tierLevel) {
      case 'quick':
        return {
          label: 'QUICK',
          bgClass: 'bg-gradient-to-r from-emerald-500 to-green-500',
          icon: <Zap className="h-3 w-3" />,
          description: '15 min processing'
        }
      case 'standard':
        return {
          label: 'STANDARD',
          bgClass: 'bg-gradient-to-r from-blue-500 to-cyan-500',
          icon: <TrendingUp className="h-3 w-3" />,
          description: '30 min processing'
        }
      case 'comprehensive':
        return {
          label: 'COMPREHENSIVE',
          bgClass: 'bg-gradient-to-r from-purple-500 to-pink-500',
          icon: <BarChart3 className="h-3 w-3" />,
          description: '45 min processing'
        }
      case 'enterprise':
        return {
          label: 'ENTERPRISE',
          bgClass: 'bg-gradient-to-r from-amber-500 to-orange-500',
          icon: <Target className="h-3 w-3" />,
          description: '60+ min processing'
        }
      default:
        return {
          label: 'STANDARD',
          bgClass: 'bg-gradient-to-r from-blue-500 to-cyan-500',
          icon: <TrendingUp className="h-3 w-3" />,
          description: '30 min processing'
        }
    }
  }

  const getServiceIcon = (serviceId: string) => {
    switch (serviceId) {
      case 'market-pulse-executive':
        return <Zap className="h-6 w-6 text-white" />
      case 'market-essentials':
        return <TrendingUp className="h-6 w-6 text-white" />
      case 'market-intelligence':
        return <BarChart3 className="h-6 w-6 text-white" />
      case 'strategic-analysis':
        return <Target className="h-6 w-6 text-white" />
      default:
        return <BarChart3 className="h-6 w-6 text-white" />
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Select Your Intelligence Service
        </h2>
        <p className="text-gray-600">
          Choose the service that best matches your strategic intelligence needs
        </p>
      </div>

      {/* Info Box */}
      <Card className="bg-blue-50 border-blue-200">
        <div className="p-4">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-600 mt-0.5" />
            <div className="text-sm text-blue-800">
              <strong>All services include:</strong> AI-powered analysis, LaTeX-generated PDF with professional typesetting,
              Chicago style formatting, and delivery within 4 hours via email. Processing times vary by service tier.
            </div>
          </div>
        </div>
      </Card>

      {/* Service Selection Grid */}
      <div className="grid md:grid-cols-2 gap-6">
        {services.map((service) => {
          const tierConfig = getTierBadgeConfig(service.tierLevel)
          const isSelected = selectedService === service.id
          const isFree = service.price.value === 0

          return (
            <Card
              key={service.id}
              className={`relative cursor-pointer transition-all duration-300 hover:shadow-xl ${
                isSelected 
                  ? 'ring-2 ring-blue-500 border-blue-500 shadow-lg' 
                  : 'hover:border-gray-300'
              }`}
              onClick={() => handleServiceSelect(service.id)}
            >
              {/* Tier Badge */}
              <div className="absolute -top-3 -right-2 z-10">
                <div className={`${tierConfig.bgClass} text-white rounded-full px-3 py-1 text-xs font-bold flex items-center gap-1 shadow-lg`}>
                  {tierConfig.icon}
                  {tierConfig.label}
                </div>
              </div>

              {/* Free Badge */}
              {isFree && (
                <div className="absolute -top-3 left-4 z-10">
                  <Badge className="bg-green-500 text-white border-0 px-3 py-1">
                    <Star className="w-3 h-3 mr-1" />
                    FREE - No Credit Card
                  </Badge>
                </div>
              )}

              <div className="p-6">
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className={`w-12 h-12 ${tierConfig.bgClass} rounded-xl flex items-center justify-center`}>
                    {getServiceIcon(service.id)}
                  </div>
                  {isSelected && (
                    <CheckCircle className="w-6 h-6 text-blue-600" />
                  )}
                </div>

                {/* Title and Description */}
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {service.name}
                </h3>
                <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                  {service.description}
                </p>

                {/* Price */}
                <div className="flex items-baseline gap-2 mb-4">
                  {(() => {
                    const priceFormatted = formatPriceWithSmallDecimals(service.price.display)
                    if (typeof priceFormatted === 'string') {
                      return (
                        <span className={`text-2xl font-bold ${
                          isFree ? 'text-green-600' : 'text-gray-900'
                        }`}>
                          {priceFormatted}
                        </span>
                      )
                    }
                    return (
                      <span className={`font-bold ${
                        isFree ? 'text-green-600' : 'text-gray-900'
                      }`}>
                        <span className="text-2xl">{priceFormatted.dollars}</span>
                        <span className="text-lg">{priceFormatted.cents}</span>
                        <span className="text-lg ml-1">{priceFormatted.currency}</span>
                      </span>
                    )
                  })()}
                  <span className="text-sm text-gray-500">
                    {isFree ? 'Limited time offer' : 'One-time payment'}
                  </span>
                </div>

                {/* Key Features */}
                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <FileText className="h-4 w-4 text-gray-400" />
                    <span>{service.metadata.specifications.documents.pageCount.display} report</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <Clock className="h-4 w-4 text-gray-400" />
                    <span>{tierConfig.description} • {service.metadata.deliveryTime} delivery</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <Download className="h-4 w-4 text-gray-400" />
                    <span>PDF exports</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <Award className="h-4 w-4 text-gray-400" />
                    <span>LaTeX-generated PDF with Chicago style</span>
                  </div>
                </div>

                {/* Consulting Value */}
                <div className="pt-4 border-t border-gray-200">
                  <div className="text-xs text-gray-500">
                    Replaces {service.metadata.specifications.quality.consultingValue} consulting engagement
                  </div>
                </div>
              </div>
            </Card>
          )
        })}
      </div>

      {/* Selected Service Details */}
      {selectedService && (
        <Card className="bg-green-50 border-green-200">
          <div className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <span className="font-semibold text-green-900">
                Selected: {services.find(s => s.id === selectedService)?.name}
              </span>
            </div>
            <p className="text-sm text-green-800">
              {services.find(s => s.id === selectedService)?.metadata.customerBenefits.primaryValue}
            </p>
          </div>
        </Card>
      )}

    </div>
  )
}